import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getAuth, signInWithRedirect, GoogleAuthProvider, OAuthProvider, getRedirectResult } from "firebase/auth";
import { setUser } from "../../State/usersSlice";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import "../../authentication/auth.css";

interface UserCredentials {
  email: string;
  password: string;
}

const AdminRegistrationForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = getAuth();
  const CLOUD_FUNCTION_URL = "https://createuser-67773hkndq-uc.a.run.app";

  const [error, setError] = useState<string | null>(null);
  const [userCredentials, setUserCredentials] = useState<UserCredentials>({
    email: "",
    password: "",
  });
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [socialProvider, setSocialProvider] = useState<string | null>(null);

  // Handle redirect result for social sign-up
  useEffect(() => {
    const handleRedirectResult = async () => {
      if (!socialProvider) return;
      try {
        setIsSubmitting(true);
        const result = await getRedirectResult(auth);
        if (result && result.user) {
          const user = result.user;
          const email = user.email;
          if (!email) {
            throw new Error("No email provided by social provider.");
          }

          // Log payload for debugging
          const payload = {
            idToken: await user.getIdToken(),
            firstName: user.displayName?.split(" ")[0] || firstName || "N/A",
            lastName: user.displayName?.split(" ")[1] || lastName || "N/A",
            provider: socialProvider,
          };
          console.log("Social sign-up payload:", payload);

          // Call cloud function for social sign-up
          const response = await axios.post(`${CLOUD_FUNCTION_URL}/social`, payload);
          console.log("Cloud function response:", response.data);

          const { uid, schoolID } = response.data;
          if (!uid || !schoolID) {
            throw new Error("Invalid response from cloud function.");
          }

          // Dispatch user to Redux
          const userState = {
            id: uid,
            email,
            role: "admin",
            school: { schoolID },
            firstName: user.displayName?.split(" ")[0] || firstName || "N/A",
            lastName: user.displayName?.split(" ")[1] || lastName || "N/A",
            selectedAffiliation: null, // Add appropriate value for selectedAffiliation
            affiliations: [], // Add appropriate value for affiliations
          };
          dispatch(setUser(userState));
          console.log("Dispatched user to Redux:", userState);

          // Navigate to dashboard
          navigate("/dashboard", { replace: true });
        }
      } catch (err: any) {
        console.error("Social sign-up error:", err);
        let errorMessage = err.response?.data?.message || err.message || "Failed to create account.";
        if (err.code) {
          switch (err.code) {
            case "auth/email-already-in-use":
              errorMessage = "This email is already registered. Please sign in.";
              break;
            case "auth/account-exists-with-different-credential":
              errorMessage = "Account exists with different credentials. Please sign in.";
              break;
            default:
              errorMessage = `Authentication error: ${err.message}`;
          }
        }
        setError(errorMessage);
        setTimeout(() => navigate("/login-page", { replace: true }), 1000);
      } finally {
        setIsSubmitting(false);
        setSocialProvider(null);
      }
    };

    handleRedirectResult();
  }, [auth, dispatch, navigate, socialProvider, firstName, lastName]);

  const handleCredentials = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setUserCredentials((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (!userCredentials.email || !userCredentials.password || !firstName || !lastName) {
        throw new Error("Please fill in all fields.");
      }

      // Generate schoolID
      const schoolID = uuidv4();

      // Log payload for debugging
      const payload = {
        email: userCredentials.email,
        password: userCredentials.password,
        firstName,
        lastName,
        schoolID,
        role: "admin",
      };
      console.log("Sending payload to cloud function:", payload);

      // Create user via Cloud Function
      const response = await axios.post(CLOUD_FUNCTION_URL, payload);
      console.log("Cloud function response:", response.data);

      const { uid, schoolID: responseSchoolID } = response.data;
      if (!uid || !responseSchoolID) {
        throw new Error("Invalid response from cloud function.");
      }

      // Dispatch user to Redux
      const userState = {
        id: uid,
        email: userCredentials.email,
        role: "admin",
        school: { schoolID: responseSchoolID },
        firstName,
        lastName,
        selectedAffiliation: null, // Add appropriate value for selectedAffiliation
        affiliations: [], // Add appropriate value for affiliations
      };
      dispatch(setUser(userState));
      console.log("Dispatched user to Redux:", userState);

      // Navigate to dashboard
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      console.error("Email sign-up error:", err);
      let errorMessage = err.response?.data?.message || err.message || "Failed to create account.";
      if (err.code) {
        switch (err.code) {
          case "auth/email-already-in-use":
            errorMessage = "This email is already registered. Please sign in.";
            break;
          case "auth/invalid-email":
            errorMessage = "Invalid email address format.";
            break;
          default:
            errorMessage = `Authentication error: ${err.message}`;
        }
      }
      setError(errorMessage);
      setTimeout(() => navigate("/login-page", { replace: true }), 1000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialSignUp = async (provider: GoogleAuthProvider | OAuthProvider, providerName: string) => {
    setError(null);
    setIsSubmitting(true);
    setSocialProvider(providerName);

    try {
      await signInWithRedirect(auth, provider);
    } catch (err: any) {
      console.error("Social sign-up redirect error:", err);
      setError(err.message || `Failed to initiate ${providerName} sign-up.`);
      setIsSubmitting(false);
      setSocialProvider(null);
      setTimeout(() => navigate("/login-page", { replace: true }), 1000);
    }
  };

  return (
    <div className="authe-container">
      <form className="authe-form" onSubmit={handleEmailSignUp}>
        <h1 className="authe-heading">Sign Up to Sonoma Cloud</h1>
        <label className="authe-label">
          <input
            type="text"
            name="first_name"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="authe-input"
          />
        </label>
        <label className="authe-label">
          <input
            type="text"
            name="last_name"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="authe-input"
          />
        </label>
        <label className="authe-label">
          <input
            type="email"
            name="email"
            placeholder="Email address"
            value={userCredentials.email}
            onChange={handleCredentials}
            className="authe-input"
          />
        </label>
        <label className="authe-label">
          <input
            type="password"
            name="password"
            placeholder="Password"
            value={userCredentials.password}
            onChange={handleCredentials}
            className="authe-input"
          />
        </label>
        <button type="submit" className="authe-submit-btn" disabled={isSubmitting}>
          {isSubmitting ? "Verifying..." : "Sign Up"}
        </button>
        {error && <p className="authe-error">{error}</p>}
        <h3 className="authe-agreement font-bold">
          By signing up, I agree to the{" "}
          <Link className="authe-link" to="/privacy-policy">
            Privacy Policy
          </Link>{" "}
          and{" "}
          <Link className="authe-link" to="/terms-of-service">
            Terms of Service
          </Link>.
        </h3>
        <h2 className="authe-or-text font-bold">Or sign up with</h2>
        <div className="authe-social-buttons-container">
          <button
            type="button"
            onClick={() => handleSocialSignUp(new GoogleAuthProvider(), "Google")}
            className="authe-social-btn-google"
            disabled={isSubmitting}
          >
            <img
              src="https://img.icons8.com/?size=50&id=17949&format=png&color=000000"
              alt="Google"
              className="social-icon"
            />
          </button>
          <button
            type="button"
            onClick={() => handleSocialSignUp(new OAuthProvider("apple.com"), "Apple")}
            className="authe-social-btn-apple"
            disabled={isSubmitting}
          >
            <img
              src="https://img.icons8.com/?size=50&id=30840&format=png&color=000000"
              alt="Apple"
              className="social-icon"
            />
          </button>
        </div>
        <h2 className="authe-bottom-text font-bold">
          Have an account? <Link to="/login-page">Sign in</Link>
        </h2>
      </form>
    </div>
  );
};

export default AdminRegistrationForm;